import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import { canAskAssistant } from "./assistantAccess";
import { askAboutError, canAskAboutError } from "./assistantErrorCapture";
import { ASSISTANT_BUSY } from "./assistantErrorToast";

const REQUEST_ID = /^[A-Za-z0-9-]{8,64}$/;
const NAME = /^[A-Za-z0-9_-]{1,64}$/;
/* The screen as the legacy left menu names it: "Leads", "Trainer Calendar". */
const SCREEN = /^[A-Za-z0-9 _.-]{1,40}$/;
/* The subheader's window registers as it mounts, beside this page; give it a few seconds. */
const WAIT_FOR_WINDOW_MS = 5000;
const POLL_MS = 100;

/**
 * What the legacy CRM's "Ask the assistant" opens: /assistant/error?requestId=…&fn=…&view=…, in a
 * new tab. The assistant lives here, so the legacy notice hands the error over by link and this page
 * asks it, the way a toast's button does here.
 *
 * Only an id, a function name and a screen name come in the link, each checked against a narrow
 * pattern; the error itself is the server's row for that id, which the backend finds for the
 * signed-in user only. A link someone else made can therefore put nothing into the question. A
 * user who is not signed in comes back here after the login (Routes keeps the link as prevUrl).
 *
 * Opened again — a reload, Back, the same link in another tab — it opens the conversation the first
 * visit started, so the answer is read in the chat and no second one is asked for (see
 * `reopenOrAskAboutError`).
 */
export const legacyError = (search) => {
    const params = new URLSearchParams(search);
    const requestId = params.get("requestId");
    const fn = params.get("fn");
    const view = params.get("view");
    if (!REQUEST_ID.test(requestId || "") && !NAME.test(fn || "")) return null;
    const screen = view && SCREEN.test(view) ? view : null;
    return {
        requestId: REQUEST_ID.test(requestId || "") ? requestId : null,
        context: {
            route: screen ? `Legacy CRM · ${screen}` : "Legacy CRM",
            at: new Date().toISOString(),
            ...(NAME.test(fn || "") ? { request: { fn, method: "POST" } } : {}),
        },
    };
};

const MESSAGES = {
    asking: {
        title: "Asking the assistant about your error",
        description: "The answer opens here. You can go back to the old CRM tab at any time.",
    },
    asked: {
        title: "The error from the old CRM is open in the assistant",
        description: "Its answer is in the assistant window. You can close this tab when you are done.",
    },
    busy: { title: "The assistant is busy", description: ASSISTANT_BUSY },
    noAccess: {
        title: "The assistant is not available to you",
        description: "Ask an administrator for the CRM Assistant role.",
    },
    noWindow: {
        title: "The assistant could not be opened",
        description: "Reload the page to try again.",
    },
    invalid: {
        title: "There is no error in this link",
        description: "Open it again from the error in the old CRM.",
    },
};

const AssistantErrorLanding = () => {
    const { search } = useLocation();
    const { user } = useSelector((state) => state.auth);
    const [error] = useState(() => legacyError(search));
    const allowed = canAskAssistant(user);
    const [state, setState] = useState(!error ? "invalid" : !allowed ? "noAccess" : "asking");
    const asked = useRef(false);

    useEffect(() => {
        if (state !== "asking" || asked.current) return undefined;
        let polls = 0;
        const timer = setInterval(() => {
            polls += 1;
            if (canAskAboutError()) {
                clearInterval(timer);
                asked.current = true;
                setState(askAboutError(error, { reopen: true }) ? "asked" : "busy");
            } else if (polls * POLL_MS >= WAIT_FOR_WINDOW_MS) {
                clearInterval(timer);
                setState("noWindow");
            }
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [state, error]);

    const { title, description } = MESSAGES[state];
    return <EmptyStatus title={title} description={description} />;
};

export default AssistantErrorLanding;
